/**
 * Список боёв карточками — им заканчиваются и карточка игрока, и страница
 * статика.
 *
 * Таблицей это было раньше: колонки не помещались на телефон, а глазами
 * сравнивать урон в узких клетках всё равно неудобно. Карточка складывается
 * вертикально и читается на любой ширине.
 */

import type { ReactNode } from 'react';

import { formatDateTime, formatRelative } from '../lib/format.ts';
import { useLinkHandler } from '../lib/router.ts';
import { OUTCOME_LABEL, outcomeColor } from '../lib/stats.ts';

/** Бой в списке: минимум, который знают обе страницы. */
export interface CaptureListItem {
  captureId: number;
  startedAt: string;
  /** Противник. `null` — наши бились третьей семьёй, и стороны не наши. */
  opponent: string | null;
  damage: number;
  kills: number;
  outcome: 'win' | 'loss' | 'unknown';
}

const number = (value: number): string => Math.round(value).toLocaleString('ru');

function CaptureItem({ capture, timezone }: { capture: CaptureListItem; timezone: string }): ReactNode {
  const onLinkClick = useLinkHandler();
  const color = outcomeColor(capture.outcome);

  return (
    <a
      href={`/stats/captures/${String(capture.captureId)}`}
      onClick={onLinkClick}
      className="flex flex-wrap items-center gap-x-4 gap-y-3 overflow-hidden rounded-xl py-3 pr-3 pl-4 transition-colors hover:bg-[var(--surface-hover)]"
      style={{
        border: '1px solid var(--border-subtle)',
        background: 'var(--surface-raised)',
        // Полоса слева — исход боя. Тот же цвет повторён плашкой рядом:
        // одним лишь цветом победу от поражения отличают не все.
        borderLeft: `3px solid ${color}`,
      }}
    >
      <div className="min-w-[10rem] flex-1">
        <div className="flex flex-wrap items-center gap-2">
          <span className="font-medium">{capture.opponent ?? 'противник неизвестен'}</span>
          <span
            className="rounded-md px-1.5 py-0.5 text-[0.65rem] font-semibold"
            style={{ color, background: `color-mix(in oklab, ${color} 15%, transparent)` }}
          >
            {OUTCOME_LABEL[capture.outcome]}
          </span>
        </div>
        <div className="mt-0.5 text-xs" style={{ color: 'var(--text-muted)' }}>
          {formatDateTime(capture.startedAt, timezone)} · {formatRelative(capture.startedAt)}
        </div>
      </div>

      {/*
        Ширина колонок задана жёстко, числа прижаты вправо: иначе блок цифр
        начинается там, где кончилось имя противника, и в соседних строках
        колонки стоят на разном отступе.
      */}
      <div className="grid w-full grid-cols-2 gap-x-4 sm:w-56 sm:shrink-0">
        <div className="text-right">
          <div className="text-sm font-semibold tabular-nums">{number(capture.damage)}</div>
          <div className="text-[0.65rem] tracking-wide uppercase" style={{ color: 'var(--text-muted)' }}>
            урон
          </div>
        </div>
        <div className="text-right">
          <div className="text-sm font-semibold tabular-nums">{number(capture.kills)}</div>
          <div className="text-[0.65rem] tracking-wide uppercase" style={{ color: 'var(--text-muted)' }}>
            киллы
          </div>
        </div>
      </div>
    </a>
  );
}

export function CaptureList({
  captures,
  timezone,
}: {
  captures: readonly CaptureListItem[];
  timezone: string;
}): ReactNode {
  return (
    <div className="flex flex-col gap-2">
      {captures.map((capture) => (
        <CaptureItem key={capture.captureId} capture={capture} timezone={timezone} />
      ))}
    </div>
  );
}
